<template>
  <div class="ticket-thread">
    <div class="thread-head">
      <div class="thread-actions">
        <button type="button" class="back-btn" @click="$emit('close')">
          ← بازگشت به لیست
        </button>
        <button
          type="button"
          class="refresh-btn"
          :disabled="loading"
          @click="$emit('refresh')"
        >
          <b-icon :icon="loading ? 'arrow-repeat' : 'arrow-clockwise'" :animation="loading ? 'spin' : null"></b-icon>
          {{ loading ? 'در حال بروزرسانی...' : 'بروزرسانی' }}
        </button>
      </div>
      <div>
        <h3>{{ ticket.name }}</h3>
        <p>
          <span>{{ ticket.ticket_ref || ('#' + ticket.id) }}</span>
          <em>{{ ticket.stage_name || 'ثبت شده' }}</em>
        </p>
      </div>
    </div>

    <p v-if="loading && !hasContent" class="muted">در حال بارگذاری پاسخ‌ها...</p>
    <div v-else class="thread-body" :class="{ 'is-refreshing': loading }">
      <div v-if="ticket.description" class="bubble is-user">
        <div class="bubble-meta">
          <strong>شما</strong>
          <small>{{ ticket.create_date || '' }}</small>
        </div>
        <p class="bubble-body">{{ ticket.description }}</p>
      </div>

      <p v-if="ticket.task && ticket.task.id" class="linked-task">
        تسک پروژه:
        <strong>{{ ticket.task.name }}</strong>
        <span v-if="ticket.task.project_name">({{ ticket.task.project_name }})</span>
        <em v-if="ticket.task.stage_name">{{ ticket.task.stage_name }}</em>
      </p>

      <p v-if="!messages.length" class="muted empty-replies">هنوز پاسخی از پشتیبانی ثبت نشده است.</p>

      <div
        v-for="message in messages"
        :key="message.id"
        class="bubble"
        :class="message.from_support ? 'is-support' : 'is-user'"
      >
        <div class="bubble-meta">
          <div class="bubble-who">
            <strong>{{ messageLabel(message) }}</strong>
            <span v-if="message.from_support && message.job_title" class="job-title">{{ message.job_title }}</span>
          </div>
          <small>{{ message.date || '' }}</small>
        </div>
        <p v-if="message.body" class="bubble-body">{{ message.body }}</p>
        <div v-if="message.attachments && message.attachments.length" class="bubble-attachments">
          <a
            v-for="file in message.attachments"
            :key="file.id"
            class="attach-thumb"
            :href="file.data_url || '#'"
            target="_blank"
            rel="noopener"
            @click.prevent="openAttachment(file)"
          >
            <img v-if="file.data_url" :src="file.data_url" :alt="file.name || 'تصویر'">
            <span v-else>{{ file.name || 'تصویر' }}</span>
          </a>
        </div>
      </div>
    </div>

    <form class="reply-form" @submit.prevent="submitReply">
      <label for="helpdesk-reply-body">پاسخ شما</label>
      <textarea
        id="helpdesk-reply-body"
        v-model="replyBody"
        rows="4"
        maxlength="5000"
        placeholder="پیام خود را برای پشتیبانی بنویسید..."
        :disabled="saving"
      ></textarea>

      <div class="attach-row">
        <input
          ref="imageInput"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          hidden
          @change="onImagesSelected"
        >
        <button type="button" class="ghost-btn attach-btn" :disabled="saving || pendingImages.length >= 3" @click="pickImages">
          <b-icon icon="image"></b-icon>
          افزودن عکس
        </button>
        <small class="attach-hint">حداکثر ۳ عکس، هر کدام تا ۲ مگابایت</small>
      </div>

      <div v-if="pendingPreviews.length" class="pending-images">
        <div v-for="(preview, index) in pendingPreviews" :key="preview.key" class="pending-item">
          <img :src="preview.url" alt="preview">
          <button type="button" class="remove-img" :disabled="saving" @click="removePendingImage(index)">×</button>
        </div>
      </div>

      <div v-if="needCaptcha" class="captcha-box">
        <div class="captcha-head">
          <span>برای ادامه، کد امنیتی را وارد کنید</span>
          <button type="button" class="ghost-btn" :disabled="captchaLoading" @click="loadCaptcha">
            {{ captchaLoading ? '...' : 'تصویر جدید' }}
          </button>
        </div>
        <div class="captcha-row">
          <img v-if="captchaImage" :src="captchaImage" alt="captcha" class="captcha-image">
          <input
            v-model="captchaAnswer"
            type="text"
            autocomplete="off"
            placeholder="کد تصویر"
            :disabled="saving || captchaLoading"
          >
        </div>
      </div>

      <p v-if="error" class="reply-error">{{ error }}</p>
      <button class="send-btn" type="submit" :disabled="saving || !canSend">
        {{ saving ? 'در حال ارسال...' : 'ارسال پیام' }}
      </button>
    </form>
  </div>
</template>

<script>
import {getAxiosInstance} from '@moeinmdn/mixitweb-front-modules/src/plugin/utils/setInstance.js'

export default {
  name: 'HelpdeskTicketThread',
  props: {
    ticket: {
      type: Object,
      required: true,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      replyBody: '',
      error: '',
      needCaptcha: false,
      captchaImage: '',
      captchaId: '',
      captchaAnswer: '',
      captchaLoading: false,
      pendingImages: [],
      pendingPreviews: [],
    }
  },
  computed: {
    hasContent() {
      return Boolean(
        (this.ticket && this.ticket.description)
        || (this.messages && this.messages.length)
        || (this.ticket && this.ticket.task && this.ticket.task.id)
      )
    },
    canSend() {
      if (!this.replyBody.trim() && !this.pendingImages.length) {
        return false
      }
      if (this.needCaptcha && !this.captchaAnswer.trim()) {
        return false
      }
      return true
    },
  },
  watch: {
    'ticket.id'() {
      this.replyBody = ''
      this.error = ''
      this.needCaptcha = false
      this.resetCaptchaFields()
      this.clearPendingImages()
    },
  },
  beforeDestroy() {
    this.clearPendingImages()
  },
  methods: {
    messageLabel(message) {
      if (!message.from_support) {
        return 'شما'
      }
      const author = (message.author || '').trim()
      if (!author) {
        return 'پشتیبانی'
      }
      const parts = author.split(',')
      if (parts.length > 1) {
        const person = parts[parts.length - 1].trim()
        if (person) {
          return person
        }
      }
      return author
    },
    openAttachment(file) {
      if (file && file.data_url) {
        window.open(file.data_url, '_blank')
      }
    },
    pickImages() {
      if (this.$refs.imageInput) {
        this.$refs.imageInput.click()
      }
    },
    onImagesSelected(event) {
      const input = event.target
      const files = input && input.files ? Array.from(input.files) : []
      if (input) {
        input.value = ''
      }
      const remaining = 3 - this.pendingImages.length
      if (remaining <= 0) {
        this.error = 'حداکثر ۳ عکس می‌توانید ارسال کنید.'
        return
      }
      const accepted = []
      for (const file of files.slice(0, remaining)) {
        if (!file.type || file.type.indexOf('image/') !== 0) {
          this.error = 'فقط فایل تصویر مجاز است.'
          continue
        }
        if (file.size > 2 * 1024 * 1024) {
          this.error = 'حجم هر عکس حداکثر ۲ مگابایت باشد.'
          continue
        }
        accepted.push(file)
      }
      if (!accepted.length) {
        return
      }
      this.error = ''
      accepted.forEach((file) => {
        this.pendingImages.push(file)
        this.pendingPreviews.push({
          key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          url: URL.createObjectURL(file),
        })
      })
    },
    removePendingImage(index) {
      const preview = this.pendingPreviews[index]
      if (preview && preview.url) {
        URL.revokeObjectURL(preview.url)
      }
      this.pendingImages.splice(index, 1)
      this.pendingPreviews.splice(index, 1)
    },
    clearPendingImages() {
      this.pendingPreviews.forEach((preview) => {
        if (preview && preview.url) {
          URL.revokeObjectURL(preview.url)
        }
      })
      this.pendingImages = []
      this.pendingPreviews = []
    },
    resetCaptchaFields() {
      this.captchaImage = ''
      this.captchaId = ''
      this.captchaAnswer = ''
    },
    loadCaptcha() {
      this.captchaLoading = true
      this.resetCaptchaFields()
      const axios = getAxiosInstance()
      return axios.post('captcha').then((res) => {
        const data = (res.data && res.data.data) || {}
        if (data.status && data.image && data.id) {
          this.needCaptcha = true
          this.captchaImage = data.image
          this.captchaId = data.id
        } else {
          this.needCaptcha = false
        }
      }).catch(() => {
        this.error = 'بارگذاری کپچا انجام نشد.'
      }).finally(() => {
        this.captchaLoading = false
      })
    },
    requireCaptcha() {
      this.needCaptcha = true
      return this.loadCaptcha()
    },
    submitReply() {
      this.error = ''
      const body = this.replyBody.trim()
      if (!body && !this.pendingImages.length) {
        this.error = 'متن پیام یا تصویر را وارد کنید.'
        return
      }
      if (this.needCaptcha && !this.captchaAnswer.trim()) {
        this.error = 'کد امنیتی را وارد کنید.'
        return
      }

      const payload = {
        ticketId: this.ticket.id,
        body,
        images: this.pendingImages.slice(),
      }
      if (this.needCaptcha) {
        payload.captcha_id = this.captchaId
        payload.captcha_answer = this.captchaAnswer.trim()
      }

      this.$emit('reply', payload, {
        onSuccess: (data) => {
          this.replyBody = ''
          this.captchaAnswer = ''
          this.clearPendingImages()
          if (data && data.captcha_status) {
            this.requireCaptcha()
          } else {
            this.needCaptcha = false
            this.resetCaptchaFields()
          }
        },
        onCaptchaRequired: () => {
          this.requireCaptcha()
          this.error = 'برای ادامه ارسال پیام، کد امنیتی را وارد کنید.'
        },
        onError: (message) => {
          this.error = message || 'ارسال پیام انجام نشد.'
          if (this.isCaptchaError(message)) {
            this.requireCaptcha()
          }
        },
      })
    },
    isCaptchaError(message) {
      const text = String(message || '')
      return text.indexOf('کپتچا') !== -1 || text.indexOf('کپچا') !== -1 || text.toLowerCase().indexOf('captcha') !== -1
    },
  },
}
</script>

<style scoped>
.ticket-thread {
  display: grid;
  gap: 18px;
}

.thread-head {
  display: grid;
  gap: 10px;
  margin-bottom: 4px;
}

.thread-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.thread-head h3 {
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 800;
  color: #1e1e1e;
}

.thread-head p {
  margin: 0;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  color: #6f5b62;
  font-size: 13px;
}

.thread-head em {
  font-style: normal;
  font-weight: 700;
  color: #eb3d60;
}

.back-btn,
.refresh-btn,
.ghost-btn,
.send-btn {
  border: 1px solid #ead7dc;
  background: #fff;
  border-radius: 999px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 13px;
}

.refresh-btn,
.send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #eb3d60;
  border-color: #eb3d60;
  color: #fff;
  font-weight: 700;
}

.refresh-btn:disabled,
.send-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.muted {
  color: #6f5b62;
  margin: 0;
}

.thread-body.is-refreshing {
  opacity: 0.72;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.thread-body {
  display: grid;
  gap: 16px;
}

.empty-replies {
  padding: 10px 0;
}

.bubble {
  border-radius: 16px;
  padding: 12px 14px;
  max-width: 92%;
  margin: 2px 0;
}

.linked-task {
  margin: 0;
  padding: 10px 12px;
  border-radius: 14px;
  background: #faf4f6;
  border: 1px solid #ead7dc;
  font-size: 13px;
  color: #1e1e1e;
  line-height: 1.7;
}

.linked-task em {
  font-style: normal;
  font-weight: 700;
  color: #eb3d60;
  margin-right: 8px;
}

.bubble.is-support {
  background: #faf4f6;
  justify-self: start;
  border: 1px solid #ead7dc;
}

.bubble.is-user {
  background: #1e1e1e;
  color: #fff;
  justify-self: end;
}

.bubble-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 12px;
}

.bubble-who {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.job-title {
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  font-size: 11px;
  font-weight: 700;
  color: #eb3d60;
  background: rgba(235, 61, 96, 0.1);
  border-radius: 999px;
  padding: 2px 8px;
  line-height: 1.4;
}

.bubble.is-support .bubble-meta {
  color: #6f5b62;
}

.bubble.is-user .bubble-meta {
  color: rgba(255, 255, 255, 0.72);
}

.bubble-body {
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.7;
  font-size: 14px;
}

.bubble-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.attach-thumb {
  display: block;
  width: 96px;
  height: 96px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(234, 215, 220, 0.9);
  background: rgba(255, 255, 255, 0.08);
  text-decoration: none;
  color: inherit;
  font-size: 11px;
  padding: 6px;
}

.attach-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.bubble.is-user .attach-thumb {
  border-color: rgba(255, 255, 255, 0.2);
}

.attach-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.attach-hint {
  color: #6f5b62;
  font-size: 12px;
}

.pending-images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pending-item {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #ead7dc;
}

.pending-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.remove-img {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 999px;
  background: rgba(30, 30, 30, 0.75);
  color: #fff;
  cursor: pointer;
  line-height: 1;
  font-size: 14px;
}

.reply-form {
  display: grid;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #ead7dc;
}

.reply-form label {
  font-size: 13px;
  font-weight: 700;
  color: #1e1e1e;
}

.reply-form textarea,
.captcha-row input {
  width: 100%;
  border: 1px solid #ead7dc;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 14px;
  resize: vertical;
  background: #fff;
  color: #1e1e1e;
}

.reply-form textarea:focus,
.captcha-row input:focus {
  outline: none;
  border-color: #eb3d60;
  box-shadow: 0 0 0 3px rgba(235, 61, 96, 0.12);
}

.captcha-box {
  display: grid;
  gap: 8px;
  padding: 12px;
  border-radius: 14px;
  background: #faf4f6;
  border: 1px solid #ead7dc;
}

.captcha-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: #6f5b62;
}

.captcha-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.captcha-image {
  height: 40px;
  border-radius: 8px;
  border: 1px solid #ead7dc;
  background: #fff;
}

.captcha-row input {
  max-width: 180px;
  border-radius: 10px;
  padding: 10px 12px;
}

.reply-error {
  margin: 0;
  color: #c62828;
  font-size: 13px;
}

.send-btn {
  justify-self: start;
  min-width: 120px;
  padding: 10px 18px;
}
</style>
